<template>
  <div
    class="time-band-group"
    :class="{ 'time-band-group--preview': hideHeader }"
  >
    <div
      v-if="!hideHeader"
      class="time-band-header"
    >
      <span>{{ title }}</span>
      <span
        v-if="revisionNote"
        class="time-band-header__revision-note"
      >
        {{ revisionNote }}
      </span>
      <YhcServiceLevelSpanPreview
        v-if="spanNote"
        :period-label="spanPeriodLabel"
      >
        <template #trigger>
          <span class="time-band-header__revision-note">{{ spanNote }}</span>
        </template>
        <slot name="spanPreview" />
      </YhcServiceLevelSpanPreview>
    </div>
    <div class="time-band-container">
      <div
        v-for="band in getTimeBandsForDisplay(timeBandPattern)"
        :key="band.id"
        class="time-band-card"
        :class="{
          'time-band-available': isTimeBandAvailable(timeBandPattern, band.id),
          'time-band-unavailable': !isTimeBandAvailable(timeBandPattern, band.id),
        }"
      >
        <div class="time-band-card-label">{{ band.label }}</div>
        <div
          v-if="!$yhcefs.isFleaMarket && !isCorporationView"
          class="time-band-card-price"
          :class="{
            'time-band-card-price--zero': getTimeBandPrice(band.id) === 0,
          }"
        >
          (+¥{{ getTimeBandPrice(band.id).toLocaleString() }})
        </div>
      </div>
      <div
        v-if="getTimeBandsForDisplay(timeBandPattern).length === 0"
        class="time-band-unavailable-text"
      >
        ※時間帯指定不可
      </div>
    </div>
    <!-- 法人向け: 3区分有料（timeBand=2）のみ有償注釈を表示 -->
    <p
      v-if="isCorporationView && timeBandCode === '2'"
      class="time-band-paid-note time-band-paid-note--danger"
    >
      ※時間帯指定有料エリアとなります
    </p>
    <!-- ユーザー向けページ: 金額と1時間枠指定のフリーダイヤル案内（法人向けでは非表示） -->
    <div
      v-if="resolvedJustOnTimeOption && isUserView && !isCorporationView"
      class="time-band-paid-note"
    >
      ※{{ resolvedJustOnTimeOption.price?.toLocaleString() }}円/件 (税込)
      にて1時間枠での指定をご希望の方は、フリーダイヤル（<a
        href="tel:0120008008"
        class="time-band-paid-note__tel"
        >0120-008008</a
      >） までお問い合わせください。
    </div>
    <!-- 法人・管理用: 料金＋説明の2行（法人向けでは非表示） -->
    <div
      v-if="resolvedJustOnTimeOption && !isUserView && !isCorporationView"
      class="time-band-paid-note"
    >
      ※{{ resolvedJustOnTimeOption.price?.toLocaleString() }}円/件 (税込)
      にて1時間単位の時間指定が可能です
    </div>
    <div
      v-if="resolvedJustOnTimeOption && !isUserView && !isCorporationView"
      class="just-on-time-notice"
    >
      {{ resolvedJustOnTimeOptionNotice }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import {
  ModelYhcOptions,
  YhcOptionsKey,
} from '../../../../models/fireModels/yhcDataTypes'
import {
  yhcEFTimeOptionsByTimeBandCode,
  YhcEFTimeOptionKey,
} from '../../YhcEstimateFormFrontPage/YhcEFUserInputDataHandler'
import { YhcEstimateFormFrontService } from '../../YhcEstimateFormFrontPage/YhcEstimateFormFrontService'
import YhcServiceLevelSpanPreview from './YhcServiceLevelSpanPreview.vue'

// 時間帯区分の型定義
interface TimeBand {
  id: string
  label: string
}

// 時間帯区分の定義
const TIME_BANDS: TimeBand[] = [
  { id: 'MORNING', label: '8-12時' },
  { id: 'AFTERNOON_EARLY', label: '12-15時' },
  { id: 'AFTERNOON_LATE', label: '15-18時' },
  { id: 'AFTERNOON_12_16', label: '12-16時' },
  { id: 'AFTERNOON_16_19', label: '16-19時' },
  { id: 'AFTERNOON_LONG', label: '12-18時' },
  { id: 'EVENING', label: '18-21時' },
]

// 時間帯区分のパターン
const TIME_BAND_PATTERNS: Record<string, string[]> = {
  THREE_FREE: ['MORNING', 'AFTERNOON_LONG', 'EVENING'], // 3区分無料
  FOUR_FREE: ['MORNING', 'AFTERNOON_EARLY', 'AFTERNOON_LATE', 'EVENING'], // 4区分無料
  FOUR_FREE_2027: ['MORNING', 'AFTERNOON_12_16', 'AFTERNOON_16_19', 'EVENING'],
  THREE_PAID: ['MORNING', 'AFTERNOON_LONG', 'EVENING'], // 3区分有料
}

// timeBandCodeとパターンのマッピング
const TIME_BAND_CODE_TO_PATTERN: Record<string, string | null> = {
  '0': 'FOUR_FREE',
  '1': 'THREE_FREE',
  '2': 'THREE_PAID',
  '3': null, // 指定不可
  '4': 'FOUR_FREE_2027',
  '5': null, // 指定不可（2027-01-01〜 / JIT可）
}

export default defineComponent({
  name: 'YhcServiceLevelTimeSlot',
  components: {
    YhcServiceLevelSpanPreview,
  },
  inject: ['YhcEstimateFormFrontServiceInstance'],
  props: {
    title: {
      type: String,
      default: '',
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    spanNote: {
      type: String,
      default: '',
    },
    spanPeriodLabel: {
      type: String,
      default: '',
    },
    timeBandCode: {
      type: String,
      required: true,
    },
    justOnTimeOption: {
      type: Object as PropType<ModelYhcOptions | null>,
      default: null,
    },
    justOnTimeOptionNotice: {
      type: String,
      default: '',
    },
    isUserView: {
      type: Boolean,
      default: false,
    },
    isCorporationView: {
      type: Boolean,
      default: false,
    },
    revisionNote: {
      type: String,
      default: '',
    },
  },
  computed: {
    $yhcefs(): YhcEstimateFormFrontService {
      return this.YhcEstimateFormFrontServiceInstance as YhcEstimateFormFrontService
    },
    // 時間帯区分パターン
    timeBandPattern(): string | null {
      return TIME_BAND_CODE_TO_PATTERN[this.timeBandCode || '3']
    },
    resolvedJustOnTimeOption(): ModelYhcOptions | null {
      if (this.justOnTimeOption) return this.justOnTimeOption
      const priceKey =
        yhcEFTimeOptionsByTimeBandCode[
          this.timeBandCode as keyof typeof yhcEFTimeOptionsByTimeBandCode
        ]?.justInTimeOption?.price
      if (!priceKey || !this.$yhcefs?.generalOptionsByKey) return null
      return this.$yhcefs.generalOptionsByKey[priceKey as YhcOptionsKey] || null
    },
    resolvedJustOnTimeOptionNotice(): string {
      if (this.justOnTimeOptionNotice) return this.justOnTimeOptionNotice
      return this.resolvedJustOnTimeOption?.notice?.replace(/<[^>]*>?/g, '') || ''
    },
  },
  methods: {
    // 表示用の時間帯区分を取得
    getTimeBandsForDisplay(patternType: string | null): TimeBand[] {
      if (!patternType) return []
      const ids = TIME_BAND_PATTERNS[patternType]
      if (!ids) return []
      return TIME_BANDS.filter((band) => ids.includes(band.id))
    },

    // 時間帯が利用可能かどうか
    isTimeBandAvailable(patternType: string | null, bandId: string): boolean {
      if (!patternType) return false
      return TIME_BAND_PATTERNS[patternType]?.includes(bandId) ?? false
    },
    // 時間帯IDからYhcEFTimeOptionKeyへのマッピング
    bandIdToTimeOptionKey(bandId: string): YhcEFTimeOptionKey | null {
      if (this.timeBandPattern === 'FOUR_FREE_2027') {
        const mapping2027: Record<string, YhcEFTimeOptionKey> = {
          MORNING: 'am8_12',
          AFTERNOON_12_16: 'pm12_16',
          AFTERNOON_16_19: 'pm16_19',
          EVENING: 'pm18_21',
        }
        return mapping2027[bandId] || null
      }
      const mapping: Record<string, YhcEFTimeOptionKey> = {
        MORNING: 'am',
        AFTERNOON_EARLY: 'pm12_15',
        AFTERNOON_LATE: 'pm15_18',
        AFTERNOON_LONG: 'pm12_18',
        EVENING: 'pm18_21',
      }
      return mapping[bandId] || null
    },
    // 時間帯の追加料金を取得（取得できない場合も0円を返す）
    getTimeBandPrice(bandId: string): number {
      if (!this.timeBandCode || !this.$yhcefs?.generalOptionsByKey) {
        return 0
      }

      const timeOptionConfig =
        yhcEFTimeOptionsByTimeBandCode[
          this.timeBandCode as keyof typeof yhcEFTimeOptionsByTimeBandCode
        ]
      if (!timeOptionConfig?.additionalPriceByTimeBand) {
        return 0
      }

      const timeOptionKey = this.bandIdToTimeOptionKey(bandId)
      if (!timeOptionKey) {
        return 0
      }

      const priceKey = timeOptionConfig.additionalPriceByTimeBand[timeOptionKey]
      if (!priceKey) {
        return 0
      }

      const price = this.$yhcefs.generalOptionsByKey[priceKey as YhcOptionsKey]?.price
      // 料金が取得できない場合も0円を返す
      return price !== undefined && price !== null ? price : 0
    },
  },
})
</script>

<style>
.time-band-group {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0.5rem;
}

.time-band-header {
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-align: left;
  color: var(--yhc-color, #1a73e8);
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem;
}

.time-band-header__revision-note {
  font-weight: 600;
  font-size: 0.75rem;
  color: #dc3545;
  line-height: 1.3;
}

.time-band-container {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.time-band-card {
  padding: 0.3rem 0.2rem;
  border-radius: 4px;
  flex: 1;
  min-width: 60px;
  text-align: center;
  font-size: 0.875rem;
  display: flex;
  flex-direction: column;
}

.time-band-card-label {
  /* ラベル部分のスタイル */
}

.time-band-card-price {
  font-size: 0.75rem;
  margin-top: 0.15rem;
  color: #dc3545;
  opacity: 0.8;
}

.time-band-card-price--zero {
  color: #000000;
}

.time-band-available {
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  color: #1890ff;
}

.time-band-unavailable {
  background-color: #f5f5f5;
  border: 1px solid #d9d9d9;
  color: #999999;
}

.time-band-unavailable-text {
  font-size: 0.875rem;
  text-align: left;
  color: #dc3545;
}

.time-band-paid-note {
  margin-top: 0.25rem;
  color: #686868;
  font-size: 0.875rem;
  text-align: left;
}

.time-band-paid-note--danger {
  color: var(--yhc-color-red, #dc3545);
}

.time-band-paid-note__tel {
  color: inherit;
  text-decoration: underline;
}

.time-band-paid-note__tel:hover {
  text-decoration: underline;
}

.just-on-time-notice {
  margin-top: 0.15rem;
  color: #686868;
  font-size: 0.75rem;
  text-align: left;
}

/* レスポンシブ対応 */
@media (max-width: 767px) {
  .time-band-container {
    flex-wrap: wrap;
  }

  .time-band-card {
    min-width: 60px;
    font-size: 0.875rem;
  }

  /* スマホ: 1時間枠注釈の文字を少し小さく */
  .time-band-paid-note {
    font-size: 0.75rem;
  }
}
</style>
